import React from "react";

const WORLDPAY_SCRIPT_URL_TEST = "https://try.access.worldpay.com/access-checkout/v2/checkout.js";
const WORLDPAY_SCRIPT_URL_LIVE = "https://access.worldpay.com/access-checkout/v2/checkout.js";

export type Checkout = {
    clearForm: (callback?: () => void) => void;
    generateSessionState: (callback: (error: any, sessionState?: string) => void) => void;
    remove: () => void;
};

export type Worldpay = {
    checkout: {
        init: (
            config: {
                id: string;
                form: string;
                fields: {
                    pan: { selector: string; placeholder?: string };
                    expiry: { selector: string; placeholder?: string };
                    cvv: { selector: string; placeholder?: string };
                };
                styles?: { [key: string]: { [key: string]: string } };
                accessibility?: {
                    ariaLabel?: {
                        pan?: string;
                        expiry?: string;
                        cvv?: string;
                    };
                    lang?: {
                        locale?: string;
                    };
                    title?: {
                        enabled: boolean;
                        pan?: string;
                        expiry?: string;
                        cvv?: string;
                    };
                };
                acceptedCardBrands?: string[];
                enablePanFormatting: boolean;
            },
            callback: (error: any, checkout?: Checkout | null) => void,
        ) => void;
    };
};

export type WithWorldpayProps = {
    worldpay: Worldpay;
};

type Props = {
    testEnvironment?: boolean;
    renderLoading?: () => React.ReactNode;
};

type State = {
    worldpay?: Worldpay;
};

type WorldpayLoadingState = {
    cancelled: boolean;
    script: HTMLScriptElement;
};

// @ts-ignore - the type checker version on this project is crying about this but its fine
type UnwrapMemo<C> = C extends React.MemoExoticComponent<infer T> ? UnwrapMemo<T> : C;

// @ts-ignore - the type checker version on this project is crying about this but its fine
type RefOf<C> = UnwrapMemo<C> extends React.ForwardRefExoticComponent<infer P>
    ? P extends React.RefAttributes<infer R>
        ? R
        : never // @ts-ignore - the type checker version on this project is crying about this but its fine
    : UnwrapMemo<C> extends React.ComponentClass<any> // @ts-ignore - the type checker version on this project is crying about this but its fine
    ? InstanceType<UnwrapMemo<C>>
    : never;

type OuterProps<C extends React.ElementType> = Omit<React.ComponentPropsWithoutRef<C>, keyof WithWorldpayProps> & Props;

type Wrapped<C extends React.ElementType> = RefOf<C> extends never
    ? React.ComponentType<OuterProps<C>>
    : React.ForwardRefExoticComponent<React.PropsWithoutRef<OuterProps<C>> & React.RefAttributes<RefOf<C>>>;
type RequiresWorldpay<C extends React.ElementType> = React.ComponentPropsWithoutRef<C> extends WithWorldpayProps ? C : never;

const REACT_FORWARD_REF_TYPE = Symbol.for("react.forward_ref");
const REACT_MEMO_TYPE = Symbol.for("react.memo");

function unwrapMemo(comp: any) {
    return comp && comp.$$typeof === REACT_MEMO_TYPE ? comp.type : comp;
}

function canReceiveRef(comp: any) {
    const inner = unwrapMemo(comp);
    return !!(inner && (inner.$$typeof === REACT_FORWARD_REF_TYPE || (inner.prototype && inner.prototype.isReactComponent)));
}

export function withWorldpay<C extends React.ElementType>(Component: RequiresWorldpay<C>): Wrapped<C> {
    type InnerProps = OuterProps<C> & { forwardedRef?: React.Ref<any> };

    // sorry for using a class component, but its needed on the main site and there's no point rewriting it for this project
    class WithWorldpay extends React.Component<InnerProps, State> {
        private loadingState?: WorldpayLoadingState;

        constructor(props: InnerProps) {
            super(props);
            this.state = {};
        }

        private loadWorldpay(testEnvironment?: boolean): void {
            const script = document.createElement("script");

            const loadingState: WorldpayLoadingState = {
                cancelled: false,
                script: script,
            };
            this.loadingState = loadingState;

            script.async = true;
            if (testEnvironment) {
                script.src = WORLDPAY_SCRIPT_URL_TEST;
            } else {
                script.src = WORLDPAY_SCRIPT_URL_LIVE;
            }

            script.onload = () => {
                if (loadingState.cancelled) return;
                this.setState(s => ({ ...s, worldpay: (window as any).Worldpay }));
            };

            document.head.appendChild(script);
        }

        private unloadWorldpay(): void {
            if (!this.loadingState) return;
            this.loadingState.cancelled = true;
            document.head.removeChild(this.loadingState.script);
            this.loadingState = undefined;
            this.setState(s => ({ ...s, worldpay: undefined }));
        }

        componentDidUpdate(prevProps: Readonly<InnerProps>): void {
            const { testEnvironment } = this.props;
            if (prevProps.testEnvironment !== testEnvironment) {
                this.unloadWorldpay();
                this.loadWorldpay(testEnvironment);
            }
        }

        componentDidMount(): void {
            const { testEnvironment } = this.props;
            this.loadWorldpay(testEnvironment);
        }

        componentWillUnmount(): void {
            this.unloadWorldpay();
        }

        render() {
            const { forwardedRef, renderLoading, testEnvironment, ...rest } = this.props;
            const { worldpay } = this.state;

            if (!worldpay) {
                const { renderLoading } = this.props;
                if (renderLoading) {
                    return renderLoading();
                }
                return null;
            }

            const props: React.ComponentPropsWithRef<C> = { ...rest, worldpay } as React.ComponentPropsWithRef<C>;

            if (forwardedRef) {
                props.ref = forwardedRef;
            }

            return <Component {...props} />;
        }
    }

    const name = (Component as any).displayName || (Component as any).name || "Component";

    if (canReceiveRef(Component)) {
        const WrappedWithRef = React.forwardRef<RefOf<C>, OuterProps<C>>((props, ref) => <WithWorldpay {...props} forwardedRef={ref} />);
        WrappedWithRef.displayName = `WithWorldpay(${name})`;
        return WrappedWithRef as Wrapped<C>;
    }

    const WrappedNoRef = (props: OuterProps<C>) => <WithWorldpay {...props} forwardedRef={undefined} />;
    (WrappedNoRef as any).displayName = `WithWorldpay(${name})`;
    return WrappedNoRef as Wrapped<C>;
}
